import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ClipboardList, Loader, ChevronLeft, Eye, CheckCircle, X,
  Users, BarChart2, FileText, Clock, Award,
} from "lucide-react";
import {
  lmsGetAnswerSheets, lmsReviewAnswerSheet,
  lmsGetSheetMarks, lmsSaveSheetMarks,
  lmsGetManualPapers, lmsGetManualPaperById,
  lmsGetExamById, lmsExtractOCR, lmsBulkIssueCerts,
} from "../api/lmsApi";
import "../lms.css";

const getUser = () => {
  try { return JSON.parse(localStorage.getItem("userData") || "{}"); } catch { return {}; }
};

// Render match_following question — shows correct pairs clearly for teacher comparison
function MatchFollowingDisplay({ text }) {
  try {
    const p = JSON.parse(text);
    if (Array.isArray(p.colA)) {
      // Build correct pairs: colA[i] → colB[answerIndex]
      const pairs = p.colA.map((a, i) => {
        const ansLetter = p.answers?.[i];
        const bIdx = ansLetter ? ansLetter.charCodeAt(0) - 97 : i;
        return { a, b: p.colB?.[bIdx] ?? "?" };
      });

      return (
        <div>
          {p.instruction && (
            <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", marginBottom: 8 }}>{p.instruction}</div>
          )}
          {/* Correct answer pairs — teacher compares these with PDF */}
          <div style={{ marginBottom: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#6b7280", marginBottom: 5, letterSpacing: 0.5 }}>
              CORRECT PAIRS — compare with student PDF
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {pairs.map((pair, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ flex: 1, padding: "5px 10px", background: "#f0f9ff", borderRadius: 6, fontSize: 12, border: "1px solid #bae6fd", fontWeight: 600 }}>
                    {i + 1}. {pair.a}
                  </div>
                  <span style={{ fontSize: 14, color: "#9ca3af", fontWeight: 700 }}>→</span>
                  <div style={{ flex: 1, padding: "5px 10px", background: "#dcfce7", borderRadius: 6, fontSize: 12, border: "1px solid #86efac", fontWeight: 600, color: "#166534" }}>
                    {pair.b}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }
  } catch {}
  return <span>{text}</span>;
}

const Q_TYPE_LABELS = {
  mcq: "MCQ", true_false: "True / False", fill_blank: "Fill in the Blank",
  short_answer: "Short Answer", long_answer: "Long Answer", match_following: "Match the Following",
};
const MANUAL_TYPES = ["short_answer", "long_answer"];

/* ── Submission Row in the table ── */
function SubmissionRow({ sheet, totalMarks, examId }) {
  const nav = useNavigate();
  const reviewed = sheet.submission_status === "reviewed";
  const pct = totalMarks && sheet.marks_obtained != null
    ? Math.round((sheet.marks_obtained / totalMarks) * 100) : null;

  return (
    <tr style={{ borderBottom: "1px solid #f3f4f6" }}>
      <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 600, color: "#111827" }}>
        {sheet.student_name || `Student #${sheet.student_id}`}
      </td>
      <td style={{ padding: "10px 14px", fontSize: 12, color: "#6b7280" }}>
        {sheet.submitted_at
          ? new Date(sheet.submitted_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
          : "—"}
      </td>
      <td style={{ padding: "10px 14px" }}>
        <span style={{
          fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
          background: reviewed ? "#dcfce7" : "#fef9c3",
          color:      reviewed ? "#166534" : "#854d0e",
        }}>
          {reviewed ? "Evaluated" : "Pending"}
        </span>
      </td>
      <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 700, color: "#111827" }}>
        {sheet.marks_obtained != null ? `${sheet.marks_obtained} / ${totalMarks || "—"}` : "—"}
      </td>
      <td style={{ padding: "10px 14px", fontSize: 13, color: "#374151" }}>
        {pct != null ? (
          <span style={{
            fontWeight: 700, padding: "2px 8px", borderRadius: 20, fontSize: 12,
            background: pct >= 80 ? "#dcfce7" : pct >= 50 ? "#fef9c3" : "#fee2e2",
            color:      pct >= 80 ? "#166534" : pct >= 50 ? "#92400e" : "#991b1b",
          }}>{pct}%</span>
        ) : "—"}
      </td>
      <td style={{ padding: "10px 14px", fontSize: 12, color: "#374151" }}>
        {sheet.grade || "—"}
      </td>
      <td style={{ padding: "10px 14px" }}>
        <div style={{ display: "flex", gap: 6 }}>
          <a href={sheet.file_url} target="_blank" rel="noreferrer" style={{
            display: "flex", alignItems: "center", gap: 4, padding: "5px 10px",
            borderRadius: 7, border: "1.5px solid #e5e7eb", background: "#fff",
            color: "#374151", fontSize: 12, fontWeight: 600, textDecoration: "none",
          }}>
            <Eye size={12} /> PDF
          </a>
          <button onClick={() => nav(`/lms/exams/${examId}/offline-results/${sheet.id}`)} style={{
            display: "flex", alignItems: "center", gap: 4, padding: "5px 12px",
            borderRadius: 7, border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer",
            background: reviewed
              ? "linear-gradient(135deg,#16a34a,#15803d)"
              : "linear-gradient(135deg,#4f46e5,#7c3aed)",
            color: "#fff",
          }}>
            <ClipboardList size={12} /> {reviewed ? "Re-evaluate" : "Evaluate"}
          </button>
        </div>
      </td>
    </tr>
  );
}

/* ── Grading Modal ── */
function GradingModal({ sheet, exam, onClose, onSaved }) {
  const [paper,       setPaper]       = useState(null);
  const [qMarks,      setQMarks]      = useState({});
  const [grade,       setGrade]       = useState(sheet.grade || "");
  const [remarks,     setRemarks]     = useState(sheet.teacher_remarks || "");
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState({});
  const [tab,         setTab]         = useState("questions");
  const [ocrText,     setOcrText]     = useState("");
  const [ocrLoading,  setOcrLoading]  = useState(false);
  const [ocrWarning,  setOcrWarning]  = useState("");

  useEffect(() => {
    const init = async () => {
      try {
        const [paperRes, marksRes] = await Promise.all([
          lmsGetManualPapers({ exam_id: sheet.exam_id }),
          lmsGetSheetMarks(sheet.id),
        ]);
        const papers = paperRes?.data?.data || [];
        if (papers[0]) {
          const full = await lmsGetManualPaperById(papers[0].id);
          setPaper(full.data);
        }
        const existing = marksRes?.data?.data || [];
        const m = {};
        existing.forEach((e) => { m[e.question_id] = { marks: String(e.marks_awarded ?? ""), remark: e.teacher_remark || "" }; });
        setQMarks(m);
      } catch {}
      finally { setLoading(false); }
    };
    init();
  }, [sheet.id, sheet.exam_id]);

  const allQuestions = paper?.sections?.flatMap((sec) =>
    (sec.questions || []).map((q) => ({
      ...q,
      sectionType:  sec.question_type,
      sectionLabel: sec.section_label,
      marksPerQ:    sec.marks_per_q,
    }))
  ) || [];

  const objQuestions = allQuestions.filter((q) => !MANUAL_TYPES.includes(q.sectionType));
  const subQuestions = allQuestions.filter((q) =>  MANUAL_TYPES.includes(q.sectionType));

  const computedTotal = allQuestions.reduce((sum, q) => {
    const m = parseFloat(qMarks[q.id]?.marks ?? "");
    return sum + (isNaN(m) ? 0 : m);
  }, 0);

  const handleSave = async () => {
    setSaving((p) => ({ ...p, _all: true }));
    try {
      if (allQuestions.length > 0) {
        const marksArr = allQuestions.map((q) => ({
          question_id:    q.id,
          marks_awarded:  parseFloat(qMarks[q.id]?.marks ?? 0) || 0,
          teacher_remark: qMarks[q.id]?.remark || null,
        }));
        await lmsSaveSheetMarks(sheet.id, { marks: marksArr });
      }
      await lmsReviewAnswerSheet(sheet.id, {
        marks_obtained:  allQuestions.length > 0 ? computedTotal : null,
        grade:           grade || null,
        teacher_remarks: remarks || null,
      });
      toast.success("Evaluation saved!");
      onSaved();
      onClose();
    } catch { toast.error("Failed to save"); }
    finally { setSaving((p) => ({ ...p, _all: false })); }
  };

  const setMark = (qid, val) =>
    setQMarks((p) => ({ ...p, [qid]: { ...p[qid], marks: val } }));
  const setRemark = (qid, val) =>
    setQMarks((p) => ({ ...p, [qid]: { ...p[qid], remark: val } }));

  // Parse OCR text into { sectionName: { qNo: answerText } }
  // Tracks SECTION headings so Q1 in MCQ doesn't overwrite Q1 in Short Answer
  const parseOCRAnswers = (text) => {
    const sections = {};
    let currentSection = "__default__";
    let currentQ = null;
    sections[currentSection] = {};

    const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
    for (const line of lines) {
      // Detect "SECTION A — Multiple Choice Questions" or "SECTION A: ..."
      const secMatch = line.match(/SECTION\s+[A-Z\d]+\s*[—–\-:]\s*(.+)/i);
      if (secMatch) {
        currentSection = secMatch[1].trim().toLowerCase();
        if (!sections[currentSection]) sections[currentSection] = {};
        currentQ = null;
        continue;
      }
      // Detect Q number: "Q1." or "Q1 "
      const qMatch = line.match(/^Q(\d+)[.\s]/i);
      if (qMatch) { currentQ = parseInt(qMatch[1], 10); continue; }
      // Detect "Answer: ..."
      const ansMatch = line.match(/^Answer\s*:\s*(.+)/i);
      if (ansMatch && currentQ != null) {
        sections[currentSection][currentQ] = ansMatch[1].trim();
      }
    }
    return sections;
  };

  // Map OCR section name keywords → paper question types
  const SECTION_TYPE_KEYWORDS = [
    { keys: ["multiple choice", "mcq"],         type: "mcq" },
    { keys: ["true", "false"],                   type: "true_false" },
    { keys: ["fill"],                            type: "fill_blank" },
    { keys: ["short answer", "short"],           type: "short_answer" },
    { keys: ["long answer", "long"],             type: "long_answer" },
    { keys: ["match"],                           type: "match_following" },
  ];

  const autoGradeFromOCR = (text) => {
    const sectionAnswers = parseOCRAnswers(text);

    // Group paper questions by type, preserving order within each type
    const paperByType = {};
    (paper?.sections || []).forEach((sec) => {
      if (!paperByType[sec.question_type]) paperByType[sec.question_type] = [];
      (sec.questions || []).forEach((q) =>
        paperByType[sec.question_type].push({ ...q, marksPerQ: sec.marks_per_q, sectionType: sec.question_type })
      );
    });

    let autoFilled = 0;
    const newMarks = {};

    for (const [sectionName, qAnswers] of Object.entries(sectionAnswers)) {
      if (!Object.keys(qAnswers).length) continue;

      // Find matching question type from section name
      let matchedType = null;
      for (const { keys, type } of SECTION_TYPE_KEYWORDS) {
        if (keys.some((k) => sectionName.includes(k))) { matchedType = type; break; }
      }
      if (!matchedType) continue;

      const paperQs = paperByType[matchedType] || [];
      for (const [qNoStr, studentAns] of Object.entries(qAnswers)) {
        const idx = parseInt(qNoStr, 10) - 1; // 0-based index within section
        const paperQ = paperQs[idx];
        if (!paperQ) continue;

        const correct  = (paperQ.correct_answer || "").trim().toLowerCase();
        const given    = studentAns.trim().toLowerCase();
        const maxMark  = parseFloat(paperQ.marks || paperQ.marksPerQ || 0);
        const isCorrect = correct.length > 0 && (
          given === correct ||
          given.startsWith(correct) ||
          correct.startsWith(given)
        );

        newMarks[paperQ.id] = {
          marks:  isCorrect ? String(maxMark) : "0",
          remark: `OCR: "${studentAns}" — ${isCorrect ? "Correct ✓" : `Wrong (Expected: ${paperQ.correct_answer || "?"})`}`,
        };
        autoFilled++;
      }
    }

    if (autoFilled > 0) setQMarks((prev) => ({ ...prev, ...newMarks }));
    return autoFilled;
  };

  const handleOCR = async () => {
    setOcrLoading(true);
    setOcrText("");
    setOcrWarning("");
    try {
      const res = await lmsExtractOCR(sheet.id);
      const { text, warning } = res.data;
      setOcrText(text || "");
      if (warning) setOcrWarning(warning);
      if (!text && !warning) {
        setOcrWarning("No text could be extracted from this file.");
      } else if (text) {
        const filled = autoGradeFromOCR(text);
        if (filled > 0) {
          setOcrWarning(`Auto-graded ${filled} objective question(s) from extracted text. Please verify and correct if needed.`);
        }
      }
    } catch (err) {
      setOcrWarning("OCR failed: " + (err?.response?.data?.message || err.message));
    } finally {
      setOcrLoading(false);
    }
  };

  const isPdf = sheet.file_url && (sheet.file_url.toLowerCase().endsWith(".pdf") || sheet.file_url.toLowerCase().includes(".pdf?"));
  const isImage = sheet.file_url && /\.(jpe?g|png|webp|gif)(\?|$)/i.test(sheet.file_url);

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(15,23,42,0.7)",
      zIndex: 1000, display: "flex", alignItems: "stretch",
    }}>
      <div style={{
        background: "#fff", width: "100%", maxWidth: 1300, margin: "0 auto",
        display: "flex", flexDirection: "column",
        boxShadow: "0 24px 80px rgba(0,0,0,0.3)", overflow: "hidden",
      }}>
        {/* Gradient bar */}
        <div style={{ height: 5, background: "linear-gradient(90deg,#4f46e5,#a855f7,#ec4899)", flexShrink: 0 }} />

        {/* Header */}
        <div style={{ padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #e5e7eb", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 11,
              background: "linear-gradient(135deg,#4f46e5,#7c3aed)",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <ClipboardList size={18} color="#fff" />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: "#111827" }}>
                {sheet.student_name || `Student #${sheet.student_id}`}
              </div>
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 1 }}>
                {exam?.name}
                <span style={{ margin: "0 6px", color: "#d1d5db" }}>|</span>
                Submitted: {sheet.submitted_at ? new Date(sheet.submitted_at).toLocaleString("en-IN") : "—"}
                {allQuestions.length > 0 && (
                  <>
                    <span style={{ margin: "0 6px", color: "#d1d5db" }}>|</span>
                    Score: <strong style={{ color: "#4f46e5" }}>{computedTotal.toFixed(1)} / {exam?.total_marks || "?"}</strong>
                  </>
                )}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <a href={sheet.file_url} target="_blank" rel="noreferrer" style={{
              display: "flex", alignItems: "center", gap: 5, padding: "6px 14px",
              borderRadius: 8, background: "#f0f9ff", color: "#0369a1", border: "1.5px solid #bae6fd",
              fontSize: 12, fontWeight: 700, textDecoration: "none",
            }}>
              <Eye size={13} /> Open in New Tab
            </a>
            <button onClick={onClose} style={{
              background: "#f3f4f6", border: "none", cursor: "pointer", color: "#6b7280",
              borderRadius: 8, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Body — split layout */}
        <div style={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0 }}>

          {/* LEFT — File Viewer */}
          <div style={{ flex: "0 0 55%", borderRight: "1px solid #e5e7eb", background: "#1e1e2e", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ padding: "8px 14px", background: "#111827", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <FileText size={14} color="#a5b4fc" />
              <span style={{ fontSize: 12, color: "#a5b4fc", fontWeight: 600 }}>Student's Answer Sheet</span>
              <span style={{ fontSize: 11, color: "#6b7280", marginLeft: "auto" }}>{sheet.file_name || "uploaded file"}</span>
            </div>
            <div style={{ flex: 1, overflow: "auto", display: "flex", alignItems: "flex-start", justifyContent: "center" }}>
              {!sheet.file_url ? (
                <div style={{ color: "#6b7280", padding: 40, textAlign: "center" }}>
                  <FileText size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
                  <div>No file uploaded</div>
                </div>
              ) : isPdf ? (
                <iframe
                  src={sheet.file_url}
                  title="Answer Sheet"
                  style={{ width: "100%", height: "100%", border: "none", minHeight: 600 }}
                />
              ) : isImage ? (
                <img
                  src={sheet.file_url}
                  alt="Answer Sheet"
                  style={{ maxWidth: "100%", display: "block", padding: 12 }}
                />
              ) : (
                <div style={{ color: "#9ca3af", padding: 40, textAlign: "center" }}>
                  <FileText size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
                  <div style={{ marginBottom: 12, fontSize: 13 }}>This file type cannot be previewed inline.</div>
                  <a href={sheet.file_url} target="_blank" rel="noreferrer" style={{
                    display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 18px",
                    borderRadius: 9, background: "#4f46e5", color: "#fff", fontWeight: 700, fontSize: 13, textDecoration: "none",
                  }}>
                    <Eye size={14} /> Download / Open File
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — Grading Panel */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {/* Tab bar */}
            <div style={{ display: "flex", borderBottom: "1px solid #e5e7eb", flexShrink: 0, background: "#fafafa" }}>
              {[
                { key: "questions", label: `All Questions (${allQuestions.length})`, desc: "Mark each question" },
                { key: "overview",  label: "Grade & Remarks",                         desc: "Overall grade & feedback" },
              ].map((t) => (
                <button key={t.key} onClick={() => setTab(t.key)} style={{
                  padding: "10px 18px 11px", border: "none", background: "none", cursor: "pointer",
                  borderBottom: `3px solid ${tab === t.key ? "#4f46e5" : "transparent"}`,
                  color: tab === t.key ? "#4f46e5" : "#6b7280",
                  fontWeight: tab === t.key ? 700 : 500, fontSize: 12,
                  display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1,
                }}>
                  <span>{t.label}</span>
                  <span style={{ fontSize: 10, color: tab === t.key ? "#a5b4fc" : "#9ca3af", fontWeight: 400 }}>{t.desc}</span>
                </button>
              ))}
            </div>

            {/* OCR Extract Bar */}
            <div style={{ padding: "8px 16px", background: "#f8fafc", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
              <FileText size={13} color="#7c3aed" />
              <span style={{ fontSize: 11, color: "#6b7280", flex: 1 }}>
                Extract text from uploaded file (typed PDF/image only)
              </span>
              <button onClick={handleOCR} disabled={ocrLoading} style={{
                display: "flex", alignItems: "center", gap: 5, padding: "5px 12px",
                borderRadius: 7, border: "1.5px solid #7c3aed", background: ocrLoading ? "#f5f3ff" : "#fff",
                color: "#7c3aed", fontSize: 11, fontWeight: 700, cursor: ocrLoading ? "not-allowed" : "pointer",
              }}>
                {ocrLoading ? <Loader size={11} className="ms2-spin" /> : <FileText size={11} />}
                {ocrLoading ? "Extracting..." : ocrText ? "Re-Extract" : "Extract Text (OCR)"}
              </button>
            </div>

            {/* OCR Result */}
            {(ocrText || ocrWarning) && (
              <div style={{ margin: "10px 16px 0", flexShrink: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                {/* Auto-grade info / warning banner */}
                {ocrWarning && (
                  <div style={{
                    padding: "8px 12px", borderRadius: 8,
                    background: ocrWarning.startsWith("Auto-graded") ? "#dcfce7" : "#fff7ed",
                    border: `1.5px solid ${ocrWarning.startsWith("Auto-graded") ? "#86efac" : "#fed7aa"}`,
                    fontSize: 11, fontWeight: 600,
                    color: ocrWarning.startsWith("Auto-graded") ? "#166534" : "#92400e",
                  }}>
                    {ocrWarning.startsWith("Auto-graded") ? "✓ " : "⚠ "}{ocrWarning}
                  </div>
                )}
                {/* Extracted text collapsible */}
                {ocrText && (
                  <details style={{ padding: "8px 12px", borderRadius: 8, background: "#f5f3ff", border: "1.5px solid #ddd6fe" }}>
                    <summary style={{ fontSize: 11, fontWeight: 700, color: "#7c3aed", cursor: "pointer", letterSpacing: 0.5 }}>
                      EXTRACTED TEXT — click to expand / collapse
                    </summary>
                    <pre style={{ fontSize: 11, color: "#374151", whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 200, overflowY: "auto", margin: "8px 0 0", fontFamily: "inherit", lineHeight: 1.6 }}>
                      {ocrText}
                    </pre>
                  </details>
                )}
              </div>
            )}

            {loading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: "60px 0", flex: 1 }}>
                <Loader size={28} className="ms2-spin" color="#4f46e5" />
              </div>
            ) : (
              <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px" }}>

                {/* ── QUESTIONS TAB ── */}
                {tab === "questions" && (() => {
                  // Helper: extract OCR student answer from remark
                  const getStudentAns = (qid) => {
                    const rem = qMarks[qid]?.remark || "";
                    if (!rem.startsWith("OCR:")) return null;
                    const m = rem.match(/^OCR:\s*"([^"]*)"/);;
                    return m ? m[1] : null;
                  };
                  const isOCRCorrect = (qid) => {
                    const rem = qMarks[qid]?.remark || "";
                    return rem.startsWith("OCR:") && rem.includes("Correct ✓");
                  };
                  const isOCRWrong = (qid) => {
                    const rem = qMarks[qid]?.remark || "";
                    return rem.startsWith("OCR:") && rem.includes("Wrong");
                  };

                  // Stats
                  const correct = objQuestions.filter((q) => isOCRCorrect(q.id)).length;
                  const wrong   = objQuestions.filter((q) => isOCRWrong(q.id)).length;
                  const hasOCR  = objQuestions.some((q) => getStudentAns(q.id) !== null);

                  return allQuestions.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af" }}>
                      <ClipboardList size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
                      <div style={{ fontSize: 14 }}>No question paper linked to this exam.</div>
                      <div style={{ fontSize: 12, marginTop: 6 }}>Switch to "Grade & Remarks" tab to enter total marks manually.</div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

                      {/* Stats bar — only shown after OCR */}
                      {hasOCR && (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 }}>
                          {[
                            { label: "Correct",  value: correct,                        color: "#16a34a", bg: "#dcfce7" },
                            { label: "Wrong",    value: wrong,                           color: "#dc2626", bg: "#fee2e2" },
                            { label: "Skipped",  value: objQuestions.length - correct - wrong, color: "#6b7280", bg: "#f3f4f6" },
                            { label: "Total",    value: objQuestions.length,             color: "#4f46e5", bg: "#eef2ff" },
                          ].map(({ label, value, color, bg }) => (
                            <div key={label} style={{ background: bg, borderRadius: 10, padding: "10px 0", textAlign: "center" }}>
                              <div style={{ fontSize: 20, fontWeight: 800, color }}>{value}</div>
                              <div style={{ fontSize: 10, fontWeight: 600, color: "#6b7280" }}>{label}</div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* All questions — online exam style */}
                      {allQuestions.map((q, i) => {
                        const isManual  = MANUAL_TYPES.includes(q.sectionType);
                        const stuAns    = getStudentAns(q.id);
                        const correct_  = isOCRCorrect(q.id);
                        const wrong_    = isOCRWrong(q.id);
                        const maxMark   = parseFloat(q.marks || q.marksPerQ || 0);
                        const graded    = qMarks[q.id];
                        const hasGrade  = graded?.marks !== undefined && graded.marks !== "";

                        // Border color by status
                        const borderColor = correct_ ? "#86efac" : wrong_ ? "#fca5a5" : "#e5e7eb";
                        const bgColor     = correct_ ? "#f0fdf4" : wrong_ ? "#fff5f5" : "#fff";

                        return (
                          <div key={q.id} style={{ border: `1.5px solid ${borderColor}`, borderRadius: 13, overflow: "hidden", background: bgColor }}>
                            {/* Question header */}
                            <div style={{
                              display: "flex", alignItems: "center", justifyContent: "space-between",
                              padding: "10px 16px",
                              background: correct_ ? "#dcfce7" : wrong_ ? "#fee2e2" : "linear-gradient(135deg,#f8fafc,#f1f5f9)",
                              borderBottom: `1px solid ${borderColor}`,
                            }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{
                                  width: 28, height: 28, borderRadius: 9,
                                  background: correct_ ? "#16a34a" : wrong_ ? "#dc2626" : "linear-gradient(135deg,#4f46e5,#7c3aed)",
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  fontSize: 10, fontWeight: 800, color: "#fff", flexShrink: 0,
                                }}>Q{q.question_no || (i + 1)}</span>
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                  {q.sectionLabel && <span style={{ fontSize: 10, fontWeight: 700, color: "#6b7280" }}>{q.sectionLabel}</span>}
                                  <span style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af" }}>
                                    {Q_TYPE_LABELS[q.sectionType] || q.sectionType}
                                  </span>
                                  <span style={{ fontSize: 10, color: "#9ca3af" }}>[{maxMark.toFixed(2)} marks]</span>
                                </div>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                {hasGrade && (
                                  <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: "#dbeafe", color: "#1e40af" }}>
                                    Graded: {graded.marks}
                                  </span>
                                )}
                                {correct_ && (
                                  <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: "#dcfce7", color: "#166534", display: "flex", alignItems: "center", gap: 4 }}>
                                    <CheckCircle size={11} /> Correct
                                  </span>
                                )}
                                {wrong_ && (
                                  <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: "#fee2e2", color: "#991b1b", display: "flex", alignItems: "center", gap: 4 }}>
                                    <X size={11} /> Wrong
                                  </span>
                                )}
                              </div>
                            </div>

                            <div style={{ padding: "14px 16px" }}>
                              {/* Question text */}
                              <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", marginBottom: 10, lineHeight: 1.7 }}>
                                {q.sectionType === "match_following"
                                  ? <MatchFollowingDisplay text={q.question_text} />
                                  : q.question_text}
                              </div>

                              {/* Student answer box */}
                              {stuAns !== null ? (
                                <div style={{ marginBottom: 10 }}>
                                  <div style={{ fontSize: 9, fontWeight: 800, color: correct_ ? "#166534" : wrong_ ? "#991b1b" : "#374151", letterSpacing: 1, marginBottom: 4 }}>
                                    STUDENT ANSWER
                                  </div>
                                  <div style={{
                                    padding: "8px 12px", borderRadius: 8,
                                    background: correct_ ? "#f0fdf4" : wrong_ ? "#fff5f5" : "#f8fafc",
                                    border: `1px solid ${correct_ ? "#86efac" : wrong_ ? "#fca5a5" : "#e5e7eb"}`,
                                    fontSize: 13, fontWeight: 600, color: "#111827",
                                  }}>
                                    {stuAns}
                                  </div>
                                  {wrong_ && q.correct_answer && (
                                    <div style={{ marginTop: 5, fontSize: 11, color: "#16a34a", fontWeight: 600 }}>
                                      Correct Answer: <strong>{q.correct_answer}</strong>
                                    </div>
                                  )}
                                </div>
                              ) : isManual ? (
                                <div style={{ marginBottom: 10, background: "#fffbeb", borderRadius: 9, padding: "10px 12px", border: "1.5px solid #fde68a" }}>
                                  <div style={{ fontSize: 9, fontWeight: 800, color: "#92400e", letterSpacing: 1, marginBottom: 4 }}>STUDENT ANSWER — SEE UPLOADED PDF</div>
                                  <div style={{ fontSize: 11, color: "#6b7280" }}>Open the PDF on the left to view the student's written answer.</div>
                                </div>
                              ) : null}

                              {/* Grading controls */}
                              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "10px 12px", background: "#f8fafc", borderRadius: 9, border: "1px solid #e5e7eb" }}>
                                <span style={{ fontSize: 11, fontWeight: 700, color: "#374151" }}>Mark as:</span>
                                <button onClick={() => setMark(q.id, String(maxMark))} style={{
                                  padding: "5px 12px", borderRadius: 7, border: "1.5px solid #16a34a",
                                  background: "#dcfce7", color: "#166534", fontSize: 11, fontWeight: 700, cursor: "pointer",
                                  display: "flex", alignItems: "center", gap: 4,
                                }}>
                                  <CheckCircle size={12} /> Correct
                                </button>
                                <button onClick={() => setMark(q.id, "0")} style={{
                                  padding: "5px 12px", borderRadius: 7, border: "1.5px solid #dc2626",
                                  background: "#fee2e2", color: "#991b1b", fontSize: 11, fontWeight: 700, cursor: "pointer",
                                  display: "flex", alignItems: "center", gap: 4,
                                }}>
                                  <X size={12} /> Wrong
                                </button>
                                <div style={{ display: "flex", alignItems: "center", gap: 5, marginLeft: "auto" }}>
                                  <span style={{ fontSize: 11, color: "#6b7280" }}>Marks:</span>
                                  <input
                                    type="number" min="0" max={maxMark || undefined} step="0.5"
                                    value={qMarks[q.id]?.marks ?? ""}
                                    onChange={(e) => setMark(q.id, e.target.value)}
                                    placeholder="0"
                                    style={{ width: 60, padding: "5px 8px", borderRadius: 7, border: "1.5px solid #d1d5db", fontSize: 13, fontWeight: 700, textAlign: "center", outline: "none" }}
                                  />
                                  <span style={{ fontSize: 11, color: "#9ca3af" }}>/ {maxMark || "?"}</span>
                                </div>
                              </div>

                              {/* Remark — only for manual/subjective */}
                              {isManual && (
                                <textarea
                                  rows={2}
                                  value={qMarks[q.id]?.remark ?? ""}
                                  onChange={(e) => setRemark(q.id, e.target.value)}
                                  placeholder="Remark for this answer (optional)..."
                                  style={{ width: "100%", marginTop: 8, padding: "7px 9px", borderRadius: 7, border: "1.5px solid #e5e7eb", fontSize: 11, resize: "vertical", color: "#374151", boxSizing: "border-box" }}
                                />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* ── OVERVIEW TAB ── */}
                {tab === "overview" && (
                  <div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", display: "block", marginBottom: 5 }}>
                          TOTAL MARKS {exam?.total_marks ? `(out of ${exam.total_marks})` : ""}
                        </label>
                        <input
                          type="number" readOnly={allQuestions.length > 0}
                          value={allQuestions.length > 0 ? computedTotal.toFixed(1) : (qMarks._manual ?? "")}
                          onChange={(e) => allQuestions.length === 0 && setQMarks((p) => ({ ...p, _manual: e.target.value }))}
                          style={{ width: "100%", padding: "8px 10px", borderRadius: 8, fontSize: 15, fontWeight: 800,
                            color: "#4f46e5", background: allQuestions.length > 0 ? "#f5f3ff" : "#fff",
                            border: "1.5px solid #e0e7ff", boxSizing: "border-box" }}
                          placeholder="Enter total marks"
                        />
                        {allQuestions.length > 0 && (
                          <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 3 }}>Auto-calculated from question marks</div>
                        )}
                      </div>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", display: "block", marginBottom: 5 }}>GRADE</label>
                        <input
                          value={grade} onChange={(e) => setGrade(e.target.value)}
                          style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1.5px solid #e5e7eb", fontSize: 13, boxSizing: "border-box" }}
                          placeholder="e.g. A, B+, Pass, Fail"
                        />
                      </div>
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", display: "block", marginBottom: 5 }}>OVERALL REMARKS</label>
                      <textarea
                        rows={5} value={remarks} onChange={(e) => setRemarks(e.target.value)}
                        style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1.5px solid #e5e7eb", fontSize: 12, resize: "vertical", boxSizing: "border-box" }}
                        placeholder="General feedback visible to the student..."
                      />
                    </div>
                  </div>
                )}

              </div>
            )}

            {/* Footer */}
            <div style={{ padding: "12px 20px", borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "flex-end", gap: 8, background: "#fafafa", flexShrink: 0 }}>
              <button onClick={onClose} style={{ padding: "8px 18px", borderRadius: 8, border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving._all} style={{
                padding: "8px 20px", borderRadius: 8, border: "none",
                background: saving._all ? "#a5b4fc" : "linear-gradient(135deg,#16a34a,#15803d)",
                color: "#fff", fontSize: 12, fontWeight: 700, cursor: saving._all ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", gap: 6,
              }}>
                {saving._all ? <Loader size={13} className="ms2-spin" /> : <CheckCircle size={13} />}
                {saving._all ? "Saving..." : "Save Evaluation"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Main Page ── */
export default function OfflineExamResults() {
  const { examId } = useParams();
  const nav = useNavigate();
  const userData = getUser();
  const schoolId = userData?.admin_id || userData?.school_id || null;

  const [exam,        setExam]        = useState(null);
  const [sheets,      setSheets]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [publishingCert, setPublishingCert] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [examRes, sheetsRes] = await Promise.all([
        lmsGetExamById(examId),
        lmsGetAnswerSheets({ exam_id: examId, school_id: schoolId }),
      ]);
      setExam(examRes.data);
      setSheets(sheetsRes?.data?.data || []);
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [examId]);

  const handlePublishCertificates = async () => {
    setPublishingCert(true);
    try {
      const res = await lmsBulkIssueCerts({ exam_id: examId, school_id: schoolId });
      toast.success(res?.data?.message || "Certificates published successfully for evaluated students!");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to publish certificates");
    } finally {
      setPublishingCert(false);
    }
  };

  const reviewed  = sheets.filter((s) => s.submission_status === "reviewed").length;
  const pending   = sheets.length - reviewed;
  const avgScore  = reviewed > 0 && exam?.total_marks
    ? (sheets.filter((s) => s.submission_status === "reviewed").reduce((sum, s) => sum + (Number(s.marks_obtained) || 0), 0) / reviewed).toFixed(1)
    : "—";

  const filtered = sheets.filter((s) => {
    const matchStatus = !statusFilter || s.submission_status === statusFilter;
    const matchSearch = !search ||
      String(s.student_id).includes(search) ||
      (s.student_name || "").toLowerCase().includes(search.toLowerCase()) ||
      (s.file_name || "").toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  return (
    <div className="lms-content-inner" style={{ paddingTop: 0 }}>
      {/* Header */}
      <div style={{ background: "#fff", borderRadius: 12, padding: "12px 18px", marginBottom: 12, border: "1px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", overflow: "hidden", position: "relative" }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg,#3b82f6,#8b5cf6,#f59e0b)" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 2 }}>
          <button onClick={() => nav(-1)} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", display: "flex", alignItems: "center", gap: 5, fontSize: 13, fontWeight: 600 }}>
            <ChevronLeft size={16} /> Back
          </button>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ fontWeight: 800, fontSize: 17, color: "#111827", lineHeight: 1.2 }}>{exam?.name || "Offline Exam Results"}</div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
              {exam?.class_name}{exam?.subject_name ? ` · ${exam?.subject_name}` : ""} · Offline Exam
              {exam?.exam_date && (
                <span style={{ marginLeft: 6 }}>
                  · {new Date(exam.exam_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                </span>
              )}
            </div>
          </div>
          {/* Publish Certificates button */}
          <button
            onClick={handlePublishCertificates}
            disabled={publishingCert}
            title="Generate and issue performance certificates to all evaluated students"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "7px 14px",
              borderRadius: 8,
              border: "1.5px solid #fde68a",
              background: "#fef3c7",
              color: "#b45309",
              fontSize: 12.5,
              fontWeight: 700,
              cursor: publishingCert ? "not-allowed" : "pointer",
              boxShadow: "0 1px 3px rgba(245,158,11,0.15)",
            }}
          >
            {publishingCert ? <Loader size={13} className="ms2-spin" /> : <Award size={14} />}
            {publishingCert ? "Publishing..." : "🏆 Publish Certificates"}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(140px,1fr))", gap: 10, marginBottom: 12 }}>
        {[
          { label: "Total Submitted", value: sheets.length,      icon: Users,         color: "#4f46e5" },
          { label: "Evaluated",       value: reviewed,            icon: CheckCircle,   color: "#16a34a" },
          { label: "Pending Review",  value: pending,             icon: Clock,         color: "#d97706" },
          { label: "Avg Score",       value: avgScore,            icon: BarChart2,     color: "#0891b2" },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} style={{ background: "#fff", borderRadius: 10, padding: "10px 14px", border: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon size={16} color={color} />
            </div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 800, color: "#111827", lineHeight: 1.1 }}>{value}</div>
              <div style={{ fontSize: 10.5, color: "#9ca3af", fontWeight: 600, marginTop: 2 }}>{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden" }}>
        <div style={{ padding: "10px 16px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#374151", flex: 1 }}>
            Student Submissions ({sheets.length})
          </div>
          {/* Search */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "5px 10px", background: "#f9fafb" }}>
            <input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search student..."
              style={{ border: "none", outline: "none", fontSize: 12, background: "transparent", width: 160, color: "#374151" }}
            />
          </div>
          {/* Status filter */}
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            style={{ padding: "6px 10px", borderRadius: 8, border: "1.5px solid #e5e7eb", fontSize: 12, color: "#374151", background: "#fff" }}>
            <option value="">All Status</option>
            <option value="submitted">Pending</option>
            <option value="reviewed">Evaluated</option>
          </select>
          <button onClick={load} style={{ padding: "6px 12px", borderRadius: 8, border: "1.5px solid #e5e7eb", background: "#fff", color: "#6b7280", cursor: "pointer", fontSize: 12 }}>
            ↻ Refresh
          </button>
        </div>

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
            <Loader size={28} className="ms2-spin" color="#4f46e5" />
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#9ca3af", fontSize: 14 }}>
            {sheets.length === 0 ? "No students have submitted answer sheets yet." : "No results match your filter."}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  {["Student", "Submitted", "Status", "Marks", "%", "Grade", "Actions"].map((h) => (
                    <th key={h} style={{ padding: "10px 14px", fontSize: 11, fontWeight: 700, color: "#9ca3af", textAlign: "left", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((sheet) => (
                  <SubmissionRow
                    key={sheet.id}
                    sheet={sheet}
                    totalMarks={exam?.total_marks}
                    examId={examId}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
}
